import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { supabase } from '../../../lib/supabase';
import { withTimeout } from '../../../lib/withTimeout';

const AUTH_TIMEOUT_MS = 15000;

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sessionReady, setSessionReady] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let mounted = true;

    const checkSession = async () => {
      try {
        const { data } = await withTimeout(
          supabase.auth.getSession(),
          AUTH_TIMEOUT_MS,
          'The reset link could not be verified. Check your connection and try again.'
        );
        if (mounted) setSessionReady(Boolean(data.session));
      } catch (sessionError: any) {
        if (mounted) setError(sessionError?.message || 'Unable to verify the reset link.');
      } finally {
        if (mounted) setCheckingSession(false);
      }
    };

    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (mounted && (event === 'PASSWORD_RECOVERY' || session)) {
        setSessionReady(true);
        setCheckingSession(false);
      }
    });

    checkSession();
    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await withTimeout(
        supabase.auth.updateUser({ password }),
        AUTH_TIMEOUT_MS,
        'The request timed out. Check your connection and try again.'
      );
      if (updateError) throw updateError;
      setSuccess(true);
      await supabase.auth.signOut();
    } catch (updateError: any) {
      setError(updateError?.message || 'Unable to update your password. Request a new reset link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" fontWeight="bold" gutterBottom>
            Reset your Makeen password
          </Typography>

          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          {success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Your password was updated. You can now sign in with the new password.
            </Alert>
          ) : null}

          {checkingSession ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress aria-label="Verifying reset link" />
            </Box>
          ) : sessionReady && !success ? (
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                required
                margin="normal"
                label="New password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={loading}
              />
              <TextField
                fullWidth
                required
                margin="normal"
                label="Confirm new password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                disabled={loading}
              />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Update password'}
              </Button>
            </Box>
          ) : !success ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This reset link is invalid or expired. Return to sign in and request a new one.
            </Alert>
          ) : null}

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link to="/auth/login">Return to sign in</Link>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
